import { map, Observable } from 'rxjs';
import {
  formInput,
  formInputPhone,
  formRow,
  formSelect,
  formSelectCountry,
  formSelectGroup,
  formTextarea,
} from '@rocketfinancialcorp/rocket-ui/form';
import { LegalIDValidator } from '@shared/validators';
import { stateListUtils, Patterns } from '@shared/utils';
import { IndustryItem } from '@shared/models';
import { BUSINESS_TYPES } from '@shared/constants';

const businessContactExpressions = {
  'props.required':
    '!!model.primaryContactLastName || !!model.primaryContactMiddleName || !!model.primaryContactEmailAddress || !!model.primaryContactPhoneNumber || !!model.primaryContactFirstName',
};

export const createBusinessAccountFields = [
  formRow([
    formInput({
      key: 'authInstanceName',
      label: 'Auth Instance',
      props: { required: true, disabled: true, minLength: 1, maxLength: 100 },
      expressions: {
        hide: "model?.accountType === 'CHILD'",
      },
    }),
    formSelect({
      key: 'authProvider',
      label: 'Auth Provider',
      props: {
        placeholder: 'Select',
        options: [
          { value: 'FIREBASE', label: 'Firebase' },
          { value: 'GCP_IDENTITY_PLATFORM', label: 'Google Cloud' },
          { value: 'KEYCLOAK', label: 'Keycloak' },
        ],
        required: true,
        disabled: true,
      },
      expressions: {
        hide: "model?.accountType === 'CHILD'",
      },
    }),
  ]),
  formRow([
    formInput({
      key: 'name',
      label: 'Company Short Name',
      props: { required: true, minLength: 4, maxLength: 20, pattern: Patterns.ALPHANUMERIC },
      validation: {
        messages: {
          pattern: 'Must contain lowercase alphanumeric and - characters',
        },
      },
    }),
    formInput({
      key: 'operatingName',
      label: 'Company Doing Business As Name',
      props: { required: false, maxLength: 100 },
    }),
  ]),
  formRow([
    formInput({
      key: 'website',
      label: 'Company Website',
      props: { pattern: Patterns.WEBSITE, required: true, maxLength: 1024 },
      validation: {
        messages: {
          pattern: 'Please enter a valid website URL. Must begin with http:// or https://',
        },
      },
    }),
  ]),
  formRow([
    formTextarea({
      key: 'description',
      label: 'Description',
      props: { required: false, maxLength: 1024 },
    }),
  ]),
];

export const createBusinessAccountAdditionalFields = (industries$: Observable<IndustryItem[]>) => {
  return [
    formRow([
      formInput({
        key: 'legalName',
        label: 'Legal Name',
        props: { required: true, minLength: 1, maxLength: 100 },
      }),
    ]),
    formRow([
      formInput({
        key: 'legalId',
        label: 'Legal ID',
        props: {
          required: false,
          placeholder: 'XXXXXXXXX',
        },
        expressions: {
          hide: 'model?.legalIdCountryCode === "USA"',
        },
      }),
      formInput({
        key: 'legalId',
        label: 'Legal ID',
        props: {
          required: false,
          maxLength: 10,
          minLength: 9,
          maskString: 'XX-XXXXXXX',
          placeholder: 'XX-XXXXXXX',
        },
        validators: {
          validation: [LegalIDValidator],
        },
        expressions: {
          hide: 'model?.legalIdCountryCode !== "USA"',
        },
      }),
      formSelectCountry({
        key: 'legalIdCountryCode',
        label: 'Legal ID Country',
        props: { placeholder: 'Select', countryRestrictions: ['US', 'AZ'], required: true },
      }),
      formSelect({
        key: 'registeredStateCode',
        label: 'Business Registered State',
        props: {
          placeholder: 'Select',
          options: stateListUtils,
          required: false,
        },
        expressions: {
          hide: "model?.legalIdCountryCode !== 'USA'",
        },
      }),
    ]),
    formRow([
      formSelect({
        key: 'legalEntityType',
        label: 'Type of Business',
        props: {
          placeholder: 'Select',
          options: BUSINESS_TYPES,
          required: false,
        },
        expressions: {
          'props.required': 'model?.legalIdCountryCode === "USA"',
        },
      }),
      formSelectGroup({
        key: 'industryCode',
        label: 'Industry',
        props: {
          placeholder: 'Select',
          groupBy: 'categoryCode',
          options: industries$.pipe(map((industries) => industries?.map((industry) => ({ ...industry })) ?? [])),
          required: true,
        },
      }),
    ]),
    formRow([
      formInput({
        key: 'email',
        label: 'Email',
        props: {
          type: 'email',
          required: true,
          pattern: Patterns.EMAIL,
          minLength: 1,
          maxLength: 100,
        },
        validation: {
          messages: {
            required: 'Email is required',
            pattern: 'Enter a valid email',
          },
        },
      }),
      formInputPhone({
        key: 'phoneNumber',
        label: 'Phone number',
        props: { required: true, minLength: 10, maxLength: 40, countryRestrictions: [], shouldKeepPhoneNumber: true },
        validators: { validation: [{ name: 'isValidPhoneNumber' }] },
      }),
    ]),
    formRow([
      formInput({ key: 'caId', label: 'Card Acceptor ID (CAID)', props: { required: false, minLength: 10, maxLength: 15 } }),
      formInput({ key: 'mcc', label: 'Merchant Category Code (MCC)', props: { minLength: 4, maxLength: 4 } }),
    ]),
  ];
};

export const updateBusinessAccountFields = (industries$: Observable<IndustryItem[]>) => {
  return [
    formRow([
      formInput({
        key: 'name',
        label: 'Company Short Name ',
        props: { required: true, minLength: 4, maxLength: 20, pattern: Patterns.ALPHANUMERIC, disabled: true },
        validation: {
          messages: {
            pattern: 'Must contain lowercase alphanumeric and - characters',
          },
        },
      }),
      formInput({
        key: 'operatingName',
        label: 'Company Doing Business As Name',
        props: { required: false, maxLength: 100 },
      }),
    ]),
    formRow([
      formInput({
        key: 'website',
        label: 'Company Website',
        props: { pattern: Patterns.WEBSITE, required: true, maxLength: 1024 },
        validation: {
          messages: {
            pattern: 'Please enter a valid website URL. Must begin with http:// or https://',
          },
        },
      }),
    ]),
    formRow([
      formTextarea({
        key: 'description',
        label: 'Description',
        props: { required: false, maxLength: 1024 },
      }),
    ]),
    formRow([
      formInput({
        key: 'legalName',
        label: 'Legal Name',
        props: { required: true, minLength: 1, maxLength: 100 },
      }),
    ]),
    formRow([
      formInput({
        key: 'legalId',
        label: 'Legal ID',
        props: {
          required: false,
          placeholder: 'XXXXXXXXX',
        },
        expressions: {
          hide: 'model?.legalIdCountryCode === "USA"',
        },
      }),
      formInput({
        key: 'legalId',
        label: 'Legal ID',
        props: {
          required: false,
          maxLength: 10,
          minLength: 9,
          maskString: 'XX-XXXXXXX',
          placeholder: 'XX-XXXXXXX',
        },
        validators: {
          validation: [LegalIDValidator],
        },
        expressions: {
          hide: 'model?.legalIdCountryCode !== "USA"',
        },
      }),
      formSelectCountry({
        key: 'legalIdCountryCode',
        label: 'Legal ID Country',
        props: { placeholder: 'Select', countryRestrictions: ['US', 'AZ'], required: true },
      }),
      formSelect({
        key: 'registeredStateCode',
        label: 'Business Registered State',
        props: {
          placeholder: 'Select',
          options: stateListUtils,
          required: false,
        },
        expressions: {
          hide: "model?.legalIdCountryCode !== 'USA'",
        },
      }),
    ]),
    formRow([
      formSelect({
        key: 'legalEntityType',
        label: 'Type of Business',
        props: {
          placeholder: 'Select',
          options: BUSINESS_TYPES,
          required: false,
        },
        expressions: {
          'props.required': 'model?.legalIdCountryCode === "USA"',
        },
      }),
      formSelectGroup({
        key: 'industryCode',
        label: 'Industry',
        props: {
          placeholder: 'Select',
          groupBy: 'categoryCode',
          options: industries$.pipe(map((industries) => industries?.map((industry) => ({ ...industry })) ?? [])),
          required: true,
        },
      }),
    ]),
    formRow([
      formInput({
        key: 'primaryEmail',
        label: 'Email',
        props: {
          type: 'email',
          required: true,
          pattern: Patterns.EMAIL,
          minLength: 1,
          maxLength: 100,
          disabled: true,
        },
        validation: {
          messages: {
            required: 'Email is required',
            pattern: 'Enter a valid email',
          },
        },
      }),
      formInputPhone({
        key: 'primaryPhoneNumber',
        label: 'Phone number',
        props: { required: true, minLength: 10, maxLength: 40, countryRestrictions: [], shouldKeepPhoneNumber: true },
        validators: { validation: [{ name: 'isValidPhoneNumber' }] },
      }),
    ]),
    formRow([
      formInput({ key: 'caId', label: 'Card Acceptor ID (CAID)', props: { required: false, minLength: 10, maxLength: 15 } }),
      formInput({ key: 'mcc', label: 'Merchant Category Code (MCC)', props: { minLength: 4, maxLength: 4 } }),
    ]),
    formRow([
      formInput({
        key: 'linxBaseUrl',
        label: 'Linx Base URL',
        props: { pattern: Patterns.WEBSITE, required: false, maxLength: 2048 },
        validation: {
          messages: {
            pattern: 'Please enter a valid URL. Must begin with http:// or https://',
          },
        },
        /* tenant-level setting — child Business Accounts inherit it and must not override it */
        expressions: {
          hide: '!model?.isTenantBusinessAccount',
        },
      }),
    ]),
  ];
};

export const businessAccountContactPersonFields = [
  formRow([
    formInput({
      key: 'primaryContactFirstName',
      label: 'First name',
      props: {
        minLength: 1,
        maxLength: 40,
      },
      expressions: businessContactExpressions,
    }),
    formInput({
      key: 'primaryContactMiddleName',
      label: 'Middle name',
      props: {
        required: false,
        minLength: 1,
        maxLength: 40,
      },
    }),
    formInput({
      key: 'primaryContactLastName',
      label: 'Last name',
      props: {
        required: false,
        minLength: 1,
        maxLength: 40,
      },
      expressions: businessContactExpressions,
    }),
  ]),
  formRow([
    formInput({
      key: 'primaryContactEmailAddress',
      label: 'Email address',
      props: {
        pattern: Patterns.EMAIL,
        type: 'email',
        minLength: 1,
        maxLength: 100,
      },
      expressions: businessContactExpressions,
      validation: {
        messages: {
          pattern: 'Enter a valid email',
        },
      },
    }),
    formInputPhone({
      key: 'primaryContactPhoneNumber',
      label: 'Phone Number',
      props: {
        minLength: 10,
        maxLength: 40,
        countryRestrictions: [],
        shouldKeepPhoneNumber: true,
      },
      expressions: businessContactExpressions,
      validators: { validation: [{ name: 'isValidPhoneNumber' }] },
    }),
  ]),
];
